import type { JSX } from 'react';

import classNames from 'classnames';

import { NotificationSource } from '../../graphql/enums.graphql';
import rowStyles from '../../pages/Support/Notifications/NotificationRowContent.module.css';
import { relativeTime } from '../../pages/Support/supportFormat';

import styles from './CenterNotificationRow.module.css';
import type { NotificationCenterNode } from './notificationCenterOps';

export const SOURCE_LABEL: Record<NotificationSource, string> = {
  [NotificationSource.Billing]: 'Billing',
  [NotificationSource.Checkins]: 'Check-ins',
  [NotificationSource.Crm]: 'CRM',
  [NotificationSource.Email]: 'Email',
  [NotificationSource.Forms]: 'Forms',
  [NotificationSource.Integrations]: 'Integrations',
  [NotificationSource.Okr]: 'OKRs',
  [NotificationSource.Tasks]: 'Tasks',
};

/**
 * One notification-center item. It shares the Support row's dot, spacing and
 * detail line so the two feeds read as one list in the bell, but the source is
 * its own small line above the title instead of a word run into it: "Email New
 * email from Acme" and "Tasks Reminder due" read as one sentence with a stutter.
 * Top to bottom: where it came from, what happened, the detail line, and when.
 */
export function CenterNotificationRow({
  notification,
}: {
  notification: NotificationCenterNode;
}): JSX.Element {
  const unread = !notification.readAt;
  const createdAt = notification.createdAt as unknown as string | Date;
  const at = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;

  return (
    <span className={rowStyles.Row}>
      <span
        className={classNames(rowStyles.Dot, unread && rowStyles.DotUnread)}
        aria-hidden="true"
      />
      <span className={rowStyles.Text}>
        <span className={styles.Category}>{SOURCE_LABEL[notification.source]}</span>
        <span className={classNames(rowStyles.Head, styles.Headline)}>
          {notification.title}
          {unread && <span className={rowStyles.VisuallyHidden}> (unread)</span>}
        </span>
        <span className={rowStyles.Meta}>
          {notification.body ? `${notification.body} · ` : ''}
          <time dateTime={at.toISOString()}>{relativeTime(at)}</time>
        </span>
      </span>
    </span>
  );
}
